import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MoreHorizontal, Plus, Search, Trash2, Users } from "lucide-react";
import { toast } from "react-toastify";
import {
  addStudentToClass,
  deleteClass,
  getClassMembers,
  getClasses,
  insertOrUpdateClass,
  removeStudentFromClass,
} from "../../services/ClassService";
import { getUsers } from "../../services/UserService";

const defaultForm = {
  name: "",
  description: "",
};

export default function Classes() {
  const [formData, setFormData] = useState(defaultForm);
  const [editingClassId, setEditingClassId] = useState("");
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isAddStudentsCardOpen, setIsAddStudentsCardOpen] = useState(false);
  const [studentSearchQuery, setStudentSearchQuery] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [isAddingStudents, setIsAddingStudents] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [isClassSettingsOpen, setIsClassSettingsOpen] = useState(false);
  const [editingClassSettings, setEditingClassSettings] = useState(null);
  const [classSettingsForm, setClassSettingsForm] = useState({
    name: "",
    description: "",
    classCode: "",
    classPin: "",
  });

  const limit = 6;

  const fetchClasses = useCallback(async (page = currentPage, query = searchQuery) => {
    try {
      const response = await getClasses(page, limit, query);
      setRows(response?.data || []);
      setCurrentPage(response?.currentPage || page);
      setTotalPages(response?.totalPages || 1);
    } catch (error) {
      const message = error?.response?.data?.message || "Không tải được danh sách lớp";
      toast.error(message);
    }
  }, [currentPage, searchQuery]);

  const fetchUsers = useCallback(async () => {
    try {
      const allUsers = [];
      const pageLimit = 200;
      let page = 1;
      let totalPages = 1;

      do {
        // Keep backend pagination order and combine all pages to avoid missing students.
        const response = await getUsers(page, pageLimit, "");
        allUsers.push(...(response?.data || []));
        totalPages = Number(response?.totalPages || 1);
        page += 1;
      } while (page <= totalPages);

      const allStudents = allUsers
        .filter((item) => item?.role === 0)
        .sort((firstUser, secondUser) => {
          const firstDate = new Date(firstUser?.createdAt || 0).getTime();
          const secondDate = new Date(secondUser?.createdAt || 0).getTime();
          return secondDate - firstDate;
        });

      setUsers(allStudents);
    } catch (error) {
      const message = error?.response?.data?.message || "Không tải được danh sách học viên";
      toast.error(message);
    }
  }, []);

  useEffect(() => {
    fetchClasses(currentPage, searchQuery);
  }, [currentPage, searchQuery, fetchClasses]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleChangeForm = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setEditingClassId("");
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("Vui lòng nhập tên lớp");
      return false;
    }

    return true;
  };

  const handleSubmitClass = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const body = {
        _id: editingClassId || undefined,
        name: formData.name,
        description: formData.description,
      };

      const response = await insertOrUpdateClass(body);
      toast.success(response?.message || "Lưu lớp thành công");
      resetForm();
      fetchClasses(1, searchQuery);
      setCurrentPage(1);
    } catch (error) {
      const message = error?.response?.data?.message || "Lưu lớp thất bại";
      toast.error(message);
    }
  };

  const handleOpenClassSettings = (classItem) => {
    setEditingClassSettings(classItem || null);
    setClassSettingsForm({
      name: classItem?.name || "",
      description: classItem?.description || "",
      classCode: classItem?.classCode || "",
      classPin: classItem?.classPin || "",
    });
    setIsClassSettingsOpen(true);
  };

  const handleCloseClassSettings = () => {
    setIsClassSettingsOpen(false);
    setEditingClassSettings(null);
    setClassSettingsForm({
      name: "",
      description: "",
      classCode: "",
      classPin: "",
    });
  };

  const handleSaveClassSettings = async () => {
    if (!editingClassSettings?._id) {
      toast.error("Không tìm thấy lớp để cập nhật");
      return;
    }

    if (!classSettingsForm.name.trim()) {
      toast.error("Vui lòng nhập tên lớp");
      return;
    }

    try {
      const body = {
        _id: editingClassSettings._id,
        name: classSettingsForm.name,
        description: classSettingsForm.description,
      };

      const response = await insertOrUpdateClass(body);
      const updatedClass = response?.data;

      setRows((prev) =>
        prev.map((item) => (item?._id === updatedClass?._id ? { ...item, ...updatedClass } : item))
      );

      if (selectedClass?._id === updatedClass?._id) {
        setSelectedClass((prev) => ({ ...prev, ...updatedClass }));
      }

      toast.success(response?.message || "Cập nhật lớp thành công");
      handleCloseClassSettings();
    } catch (error) {
      const message = error?.response?.data?.message || "Cập nhật lớp thất bại";
      toast.error(message);
    }
  };

  const handleRegeneratePin = async () => {
    if (!editingClassSettings?._id) {
      toast.error("Không tìm thấy lớp để regenerate PIN");
      return;
    }

    try {
      const response = await insertOrUpdateClass({
        _id: editingClassSettings._id,
        name: classSettingsForm.name,
        description: classSettingsForm.description,
        regeneratePin: true,
      });

      const updatedClass = response?.data;
      setClassSettingsForm((prev) => ({
        ...prev,
        classPin: updatedClass?.classPin || prev.classPin,
      }));

      setRows((prev) =>
        prev.map((item) => (item?._id === updatedClass?._id ? { ...item, ...updatedClass } : item))
      );

      if (selectedClass?._id === updatedClass?._id) {
        setSelectedClass((prev) => ({ ...prev, ...updatedClass }));
      }

      toast.success("Regenerate mã PIN thành công");
    } catch (error) {
      const message = error?.response?.data?.message || "Regenerate mã PIN thất bại";
      toast.error(message);
    }
  };

  const handleDeleteClass = async (classId) => {
    const confirmed = window.confirm("Bạn có chắc chắn muốn xóa lớp này?");
    if (!confirmed) {
      return;
    }

    try {
      const response = await deleteClass(classId);
      toast.success(response?.message || "Xóa lớp thành công");
      if (selectedClass?._id === classId) {
        setSelectedClass(null);
        setMembers([]);
      }
      fetchClasses(currentPage, searchQuery);
    } catch (error) {
      const message = error?.response?.data?.message || "Xóa lớp thất bại";
      toast.error(message);
    }
  };

  const handleOpenMembers = async (classItem) => {
    try {
      const response = await getClassMembers(classItem?._id);
      setSelectedClass(classItem);
      setMembers(response?.data || []);
      setIsAddStudentsCardOpen(false);
      setStudentSearchQuery("");
      setSelectedStudentIds([]);
      setMemberSearchQuery("");
    } catch (error) {
      const message = error?.response?.data?.message || "Không tải được danh sách học viên";
      toast.error(message);
    }
  };

  const handleAddStudentsBulk = async () => {
    if (!selectedClass?._id) {
      toast.error("Vui lòng chọn lớp");
      return;
    }

    if (selectedStudentIds.length === 0) {
      toast.error("Vui lòng chọn học viên");
      return;
    }

    try {
      setIsAddingStudents(true);
      const results = await Promise.allSettled(
        selectedStudentIds.map((studentId) => addStudentToClass(selectedClass._id, studentId))
      );

      const successCount = results.filter((item) => item.status === "fulfilled").length;
      const failedCount = results.length - successCount;

      const memberResponse = await getClassMembers(selectedClass._id);
      const updatedMembers = memberResponse?.data || [];
      setMembers(updatedMembers);

      setRows((prev) =>
        prev.map((item) =>
          item._id === selectedClass._id
            ? {
                ...item,
                memberCount: updatedMembers.length,
                members: updatedMembers,
              }
            : item
        )
      );

      setSelectedClass((prev) =>
        prev
          ? {
              ...prev,
              memberCount: updatedMembers.length,
              members: updatedMembers,
            }
          : prev
      );

      if (successCount > 0 && failedCount === 0) {
        toast.success(`Đã thêm ${successCount} học viên thành công`);
      } else if (successCount > 0 && failedCount > 0) {
        toast.warning(`Đã thêm ${successCount} học viên, ${failedCount} học viên thêm thất bại`);
      } else {
        toast.error("Không thể thêm học viên nào");
      }

      setSelectedStudentIds([]);
      setStudentSearchQuery("");
      setIsAddStudentsCardOpen(false);
    } catch (error) {
      const message = error?.response?.data?.message || "Thêm học viên thất bại";
      toast.error(message);
    } finally {
      setIsAddingStudents(false);
    }
  };

  const handleRemoveStudent = async (userId) => {
    if (!selectedClass?._id) {
      return;
    }

    try {
      const response = await removeStudentFromClass(selectedClass._id, userId);
      toast.success(response?.message || "Xóa học viên thành công");
      const updatedMembers = response?.data?.members || [];
      setMembers(updatedMembers);
      setRows((prev) => prev.map((item) => (item._id === selectedClass._id ? response?.data : item)));
    } catch (error) {
      const message = error?.response?.data?.message || "Xóa học viên thất bại";
      toast.error(message);
    }
  };

  const availableUsers = useMemo(() => {
    const memberSet = new Set((members || []).map((item) => item?._id));
    return users.filter((item) => !memberSet.has(item?._id));
  }, [members, users]);

  const suggestedUsers = useMemo(() => {
    const keyword = studentSearchQuery.trim().toLowerCase();
    if (!keyword) {
      return availableUsers;
    }

    return availableUsers
      .filter((item) => {
        const fullName = String(item?.fullName || "").toLowerCase();
        const email = String(item?.email || "").toLowerCase();
        return fullName.includes(keyword) || email.includes(keyword);
      });
  }, [availableUsers, studentSearchQuery]);

  const filteredMembers = useMemo(() => {
    const keyword = memberSearchQuery.trim().toLowerCase();
    if (!keyword) {
      return members;
    }

    return (members || []).filter((item) => {
      const fullName = String(item?.fullName || "").toLowerCase();
      const email = String(item?.email || "").toLowerCase();
      return fullName.includes(keyword) || email.includes(keyword);
    });
  }, [memberSearchQuery, members]);

  const handleToggleStudentSelection = (userId) => {
    setSelectedStudentIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      return [...prev, userId];
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Quản lý Class Private</h2>

      <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChangeForm}
            placeholder="Tên lớp"
            className="px-3 py-2 border border-gray-200 rounded-lg"
          />
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChangeForm}
            placeholder="Mô tả lớp"
            className="px-3 py-2 border border-gray-200 rounded-lg"
          />
        </div>

        <p className="text-xs text-gray-500">
          Khi tạo lớp hệ thống sẽ tự generate mã PIN 6 số cho lớp.
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleSubmitClass}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            {editingClassId ? "Cập nhật lớp" : "Tạo lớp private"}
          </button>
          {editingClassId ? (
            <button
              onClick={resetForm}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
            >
              Hủy chỉnh sửa
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Tìm theo tên lớp, mã lớp..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg"
          />
        </div>
        <button
          onClick={() => {
            setCurrentPage(1);
            fetchClasses(1, searchQuery);
          }}
          className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 font-medium"
        >
          Tìm kiếm
        </button>
      </div>

      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Tên lớp</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Mã lớp</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Mô tả</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Học viên</th>
                <th className="px-5 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Hành động</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length > 0 ? (
                rows.map((classItem) => (
                  <tr key={classItem?._id} className="hover:bg-gray-50">
                    <td className="px-5 py-4 text-sm text-gray-900 font-semibold">{classItem?.name}</td>
                    <td className="px-5 py-4 text-sm text-gray-700">{classItem?.classCode}</td>
                    <td className="px-5 py-4 text-sm text-gray-600">{classItem?.description || "-"}</td>
                    <td className="px-5 py-4 text-sm text-gray-700">{classItem?.memberCount || 0}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenMembers(classItem)}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm flex items-center gap-1"
                        >
                          <Users size={14} />
                          Học viên
                        </button>
                        <button
                          onClick={() => handleOpenClassSettings(classItem)}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm flex items-center gap-1"
                        >
                          <MoreHorizontal size={14} />
                          More
                        </button>
                        <button
                          onClick={() => handleDeleteClass(classItem?._id)}
                          className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-sm flex items-center gap-1"
                        >
                          <Trash2 size={14} />
                          Xóa
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    Chưa có lớp nào
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end items-center gap-2">
        <button
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          className="px-3 py-2 border rounded-lg disabled:opacity-40"
        >
          Prev
        </button>
        <span className="text-sm text-gray-600">
          Trang {currentPage}/{totalPages}
        </span>
        <button
          disabled={currentPage >= totalPages}
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          className="px-3 py-2 border rounded-lg disabled:opacity-40"
        >
          Next
        </button>
      </div>

      {selectedClass ? (
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-lg font-bold text-gray-900">Học viên trong lớp: {selectedClass?.name}</p>
              <p className="text-sm text-gray-500">Mã lớp: {selectedClass?.classCode}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsAddStudentsCardOpen((prev) => !prev)}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 font-medium"
              >
                Thêm học viên
              </button>
              <button
                onClick={() => {
                  setSelectedClass(null);
                  setMembers([]);
                  setIsAddStudentsCardOpen(false);
                  setStudentSearchQuery("");
                  setSelectedStudentIds([]);
                  setMemberSearchQuery("");
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Đóng
              </button>
            </div>
          </div>

          {isAddStudentsCardOpen ? (
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
              <p className="text-sm font-semibold text-gray-800">Tìm học viên và thêm hàng loạt</p>

              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={studentSearchQuery}
                  onChange={(event) => setStudentSearchQuery(event.target.value)}
                  placeholder="Nhập tên hoặc email học viên..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>

              <div className="border border-gray-200 rounded-lg bg-white max-h-64 overflow-y-auto divide-y divide-gray-100">
                {suggestedUsers.length > 0 ? (
                  suggestedUsers.map((item) => {
                    const isChecked = selectedStudentIds.includes(item?._id);
                    return (
                      <button
                        key={item?._id}
                        type="button"
                        onClick={() => handleToggleStudentSelection(item?._id)}
                        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 text-left"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item?.fullName}</p>
                          <p className="text-xs text-gray-500">{item?.email}</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          readOnly
                          className="w-4 h-4"
                        />
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-gray-500">Không tìm thấy học viên phù hợp</div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600">Đã chọn: {selectedStudentIds.length} học viên</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedStudentIds([])}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white text-sm"
                  >
                    Bỏ chọn
                  </button>
                  <button
                    onClick={handleAddStudentsBulk}
                    disabled={isAddingStudents || selectedStudentIds.length === 0}
                    className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 font-medium text-sm disabled:opacity-50"
                  >
                    {isAddingStudents ? "Đang thêm..." : `Thêm ${selectedStudentIds.length} học viên`}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="bg-white border border-gray-100 rounded-lg p-3">
            <div className="relative max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={memberSearchQuery}
                onChange={(event) => setMemberSearchQuery(event.target.value)}
                placeholder="Tìm học viên đã có trong lớp..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>
          </div>

          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Họ tên</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Email</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredMembers.length > 0 ? (
                  filteredMembers.map((item) => (
                    <tr key={item?._id}>
                      <td className="px-4 py-3 text-sm text-gray-900">{item?.fullName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item?.email}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleRemoveStudent(item?._id)}
                          className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-sm"
                        >
                          Xóa khỏi lớp
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-gray-500 text-sm">
                      {members.length > 0 ? "Không tìm thấy học viên phù hợp" : "Chưa có học viên trong lớp"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {isClassSettingsOpen ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-100 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-lg font-bold text-gray-900">Chỉnh sửa lớp học</p>
              <button
                onClick={handleCloseClassSettings}
                className="px-2 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Đóng
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tên lớp</label>
                <input
                  type="text"
                  value={classSettingsForm.name}
                  onChange={(event) =>
                    setClassSettingsForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Mô tả</label>
                <input
                  type="text"
                  value={classSettingsForm.description}
                  onChange={(event) =>
                    setClassSettingsForm((prev) => ({ ...prev, description: event.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Mã lớp</label>
                <input
                  type="text"
                  value={classSettingsForm.classCode}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Mã PIN hiện tại</label>
                <input
                  type="text"
                  value={classSettingsForm.classPin || ""}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
                />
              </div>

              <button
                type="button"
                onClick={handleRegeneratePin}
                className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              >
                Regenerate PIN
              </button>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={handleCloseClassSettings}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleSaveClassSettings}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium"
              >
                Lưu thay đổi
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
